import {
  PRESENCE_CHECKPOINT_MS,
  PRESENCE_SEGMENT_MAX_MS,
} from "@lib/usageMetrics/constants";
import { type PageFamily } from "@lib/usageMetrics/pages";

export interface PresenceSegment {
  segmentId: string;
  startedAtMs: number;
  endedAtMs: number;
  inputMinutes: number[];
  pointerMinutes: number[];
  /** The page family the segment was on, fixed at open; null when the
   *  builder was never told a page. */
  page: string | null;
  roundRef: string | null;
  /** The segment this one continues without a break — set by the 24 h
   *  rollover and the page rollover, which open the next segment at the
   *  instant the previous one ended. The read side chains the two into one
   *  stretch on this marker alone: two independent opens that happen to
   *  touch in time are never chained. */
  continuesSegmentId: string | null;
}

export type PresenceInputKind = "deliberate" | "pointer";

export interface PresenceBuilder {
  setVisible(visible: boolean): void;
  noteInput(kind: PresenceInputKind): void;
  /**
   * The person navigated. A segment is one page, so an open segment on a
   * different page is settled and a new one opened at the very instant it
   * ended — the same continuation the 24 h rollover uses, so the new page
   * earns no opening credit. Same page: no-op. Closed: remembered for the
   * next open.
   */
  setPage(page: PageFamily): void;
  tick(): void;
  close(): void;
  isOpen(): boolean;
}

export function createPresenceBuilder(opts: {
  now: () => number;
  generateSegmentId: () => string;
  onSegment: (segment: PresenceSegment) => void;
  checkpointMs?: number;
  maxSegmentMs?: number;
  /** The page at creation; `setPage` updates it. */
  page?: PageFamily;
}): PresenceBuilder {
  const checkpointMs = opts.checkpointMs ?? PRESENCE_CHECKPOINT_MS;
  const maxSegmentMs = opts.maxSegmentMs ?? PRESENCE_SEGMENT_MAX_MS;

  let isOpen = false;
  let segmentId: string = "";
  let startedAtMs: number = 0;
  let endedAtMs: number = 0;
  let inputMinutes: number[] = [];
  let pointerMinutes: number[] = [];
  // The page the next segment opens on, and the one the open segment is on.
  let currentPage: PageFamily | null = opts.page ?? null;
  let segmentPage: PageFamily | null = null;
  let continuesSegmentId: string | null = null;
  let lastTickMs: number = 0;
  let lastCheckpointEndMs: number = 0;
  // The latest instant this segment has observed on any path (tick or
  // input). A `now` behind it means the clock was set back.
  let lastObservedMs: number = 0;

  const emitSegment = () => {
    opts.onSegment({
      segmentId,
      startedAtMs,
      endedAtMs,
      inputMinutes: [...inputMinutes],
      pointerMinutes: [...pointerMinutes],
      page: segmentPage?.page ?? null,
      roundRef: segmentPage?.roundRef ?? null,
      continuesSegmentId,
    });
  };

  /**
   * Drop the minute offsets the settled end no longer covers, in exactly the
   * terms the write route checks: minute `m` names the instant
   * `startedAtMs + m * 60_000`, kept while that instant is no later than the
   * end (an input at the very instant of the last checkpoint is inside the
   * segment that checkpoint described) and while `m` is below the 24 h
   * bound (at the cap the end is `startedAtMs + 24 h`, so minute 1440 would
   * otherwise survive and fail validation, taking the whole coalesced
   * segment down with it).
   */
  const trimOffsetsTo = (endMs: number) => {
    const spanMs = endMs - startedAtMs;
    const inside = (offset: number) =>
      offset * 60_000 <= spanMs && offset * 60_000 < maxSegmentMs;
    inputMinutes = inputMinutes.filter(inside);
    pointerMinutes = pointerMinutes.filter(inside);
  };

  /**
   * More than two checkpoints since the last tick means the machine slept or
   * the clock jumped forward: the interval stops with the machine, so the
   * elapsed time is not time the page was watched. A clock set BACK is the
   * same verdict for the opposite reason: every offset and end this segment
   * already holds was computed on the old clock, and anything appended on
   * the new one would name an earlier minute — the write route rejects a
   * non-increasing array, and a shorter end is refused by the endedAt-grows
   * guard, so the segment would be poisoned for as long as it stayed open.
   */
  const isStale = (now: number): boolean =>
    now < lastObservedMs || now - lastTickMs > 2 * checkpointMs;

  /** Pull the end back to the last instant this client actually observed,
   *  dropping whatever offsets that retraction leaves unvouched. */
  const retreatToLastCheckpoint = () => {
    endedAtMs = lastCheckpointEndMs > 0 ? lastCheckpointEndMs : startedAtMs;
    trimOffsetsTo(endedAtMs);
  };

  /**
   * Settle the segment's end at `now`, the one place that decides what end a
   * write may carry. Every emit — checkpoint, rollover, close — runs through
   * here, because the write route rejects a span past the max age or an
   * offset the span no longer covers, and a rejected write takes the whole
   * coalesced segment with it (a close replaces the queued checkpoints of
   * its id). Three cases:
   * - the page has stopped ticking (sleep, clock jump): the end retreats to
   *   the last checkpoint, since `now` would bill the whole sleep;
   * - the segment has reached its max age: the end is held at the cap, the
   *   way the rollover tick does it — a hide or pagehide racing that tick
   *   must not send a longer span than the tick would have;
   * - otherwise the end is `now`.
   * The first two pull the end back, so they also drop the offsets the
   * shorter span no longer covers; the third only ever grows it, and an
   * offset recorded up to `now` is inside it by construction. Returns
   * whether the segment has reached its max age, so `tick` can reopen.
   */
  const settleEnd = (now: number): { rolledOver: boolean } => {
    if (isStale(now)) {
      retreatToLastCheckpoint();
      return { rolledOver: false };
    }

    if (now - startedAtMs >= maxSegmentMs) {
      endedAtMs = startedAtMs + maxSegmentMs;
      trimOffsetsTo(endedAtMs);
      return { rolledOver: true };
    }

    endedAtMs = now;
    return { rolledOver: false };
  };

  /** The one close path, shared by `close()` and `setVisible(false)`. */
  const closeSegment = () => {
    settleEnd(opts.now());
    emitSegment();
    isOpen = false;
  };

  /** Open a fresh segment at `startMs`, observed at `nowMs`. The rollover
   *  passes the capped end as the start so the next segment begins exactly
   *  where the previous one stopped: the page was visible throughout, and
   *  that shared instant is how the read side recognises the pair as one
   *  stretch. The tick clock, though, starts from the real now: measured
   *  from a cap up to two checkpoints in the past, the stale slack would be
   *  half spent before the new segment's first tick, and a merely delayed
   *  tick (a background tab's throttled timer) would read as a sleep — which
   *  breaks the fingerprint and hands the continuation the opening credit
   *  the rollover exists to withhold. */
  const openSegment = (
    nowMs: number = opts.now(),
    startMs: number = nowMs,
    continues: string | null = null,
  ) => {
    isOpen = true;
    continuesSegmentId = continues;
    segmentId = opts.generateSegmentId();
    startedAtMs = startMs;
    endedAtMs = startedAtMs;
    inputMinutes = [];
    pointerMinutes = [];
    segmentPage = currentPage;
    lastTickMs = nowMs;
    lastCheckpointEndMs = 0;
    lastObservedMs = nowMs;
  };

  const samePage = (a: PageFamily | null, b: PageFamily | null): boolean =>
    a?.page === b?.page && a?.roundRef === b?.roundRef;

  return {
    setVisible(visible: boolean): void {
      if (visible) {
        if (!isOpen) {
          openSegment();
        }
        // no-op if already open
      } else {
        if (isOpen) {
          closeSegment();
        }
        // no-op if already closed
      }
    },

    noteInput(kind: PresenceInputKind): void {
      if (!isOpen) {
        return;
      }

      const now = opts.now();

      // Input on a clock that went backward is not recorded: it would name a
      // minute before ones already held. The next tick settles the segment
      // at its last checkpoint and reopens on the corrected clock.
      if (now < lastObservedMs) {
        return;
      }

      // Input that arrives where a tick would have ended this segment —
      // after a sleep that beat the overdue tick, or past the 24 h cap before
      // the rollover tick — settles the old segment exactly as that tick
      // would, and opens the new one to receive this input: written onto
      // the old segment it would only be trimmed away (with the sleep, or as
      // minute 1440), losing the first thing the person did on waking or
      // after the day mark.
      if (
        now - lastTickMs > 2 * checkpointMs ||
        now - startedAtMs >= maxSegmentMs
      ) {
        const { rolledOver } = settleEnd(now);
        emitSegment();
        // A rollover reopens at the cap, the way the tick does. A wake
        // reopens at THIS `now`, the instant the offset below is computed
        // from: a fresh clock read here could land after the enqueue and put
        // the offset at -1, which the write route rejects — and with it
        // every later checkpoint of the new segment.
        openSegment(
          now,
          rolledOver ? endedAtMs : now,
          rolledOver ? segmentId : null,
        );
      }

      lastObservedMs = now;
      const offset = Math.floor((now - startedAtMs) / 60_000);

      if (kind === "deliberate") {
        const lastElement = inputMinutes[inputMinutes.length - 1];

        if (lastElement !== offset) {
          inputMinutes.push(offset);
        }
      } else if (kind === "pointer") {
        const lastElement = pointerMinutes[pointerMinutes.length - 1];

        if (lastElement !== offset) {
          pointerMinutes.push(offset);
        }
      }

      endedAtMs = now;
    },

    setPage(page: PageFamily): void {
      const previous = currentPage;
      currentPage = page;

      if (!isOpen || samePage(previous, page)) {
        return;
      }

      // Settled exactly as a tick would; a segment that turns out to have
      // slept is finished at its last checkpoint and the new page opens at
      // now (a wake, not a continuation), otherwise the new page begins at
      // the instant the old one ended.
      const now = opts.now();
      const stale = isStale(now);
      const { rolledOver } = settleEnd(now);
      emitSegment();

      if (stale) {
        openSegment(now, now, null);
        return;
      }

      if (rolledOver) {
        // The old page ran past its cap before the rollover tick noticed:
        // the stretch from the cap to this navigation was still on the old
        // page, so a continuation on that page covers it, and only then
        // does the new page open — at the navigation, not at the cap.
        currentPage = previous;
        openSegment(now, endedAtMs, segmentId);
        endedAtMs = now;
        emitSegment();
        currentPage = page;
      }

      openSegment(now, endedAtMs, segmentId);
    },

    tick(): void {
      if (!isOpen) {
        return;
      }

      const now = opts.now();
      const stale = isStale(now);
      const { rolledOver } = settleEnd(now);
      emitSegment();

      if (!stale) {
        lastObservedMs = now;
      }

      // A segment that slept or aged out is finished; the next stretch gets
      // a fresh id — from the capped end for a rollover, from now after a
      // sleep.
      if (rolledOver) {
        openSegment(now, endedAtMs, segmentId);
        return;
      }

      if (stale) {
        openSegment(now);
        return;
      }

      lastCheckpointEndMs = now;
      lastTickMs = now;
    },

    close(): void {
      if (isOpen) {
        closeSegment();
      }
    },

    isOpen(): boolean {
      return isOpen;
    },
  };
}
